import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ShoppingBag, User, Search, Heart } from 'lucide-react';
import { DiamondIcon } from './DiamondIcon';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { totalItems } = useCart();
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('resize', checkScreenSize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Shop', path: '/shop' },
    { name: 'Lookbook', path: '/lookbook' },
    { name: 'Custom Jewelry', path: '/custom-jewelry' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="text-xl lg:text-2xl font-serif text-gold flex items-center gap-2">
            <DiamondIcon className="w-6 h-6 lg:w-8 lg:h-8" />
            <span className="hidden sm:inline">Lustre & Co.</span>
            <span className="sm:hidden">Lustre</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.path) ? 'text-gold' : 'text-gray-600 hover:text-gold'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Tablet Navigation - Compact horizontal menu */}
          <nav className="hidden md:flex lg:hidden items-center space-x-4">
            {navLinks.slice(0, 4).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-xs font-medium transition-colors ${
                  isActive(link.path) ? 'text-gold' : 'text-gray-600 hover:text-gold'
                }`}
              >
                {link.name}
              </Link>
            ))}
            {/* Dropdown for remaining items on tablet */}
            <div className="relative group">
              <button className="text-xs font-medium text-gray-600 hover:text-gold transition-colors">
                More
              </button>
              <div className="absolute top-full right-0 mt-2 w-32 bg-white shadow-lg rounded-lg py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                {navLinks.slice(4).map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block px-3 py-1 text-xs font-medium transition-colors ${
                      isActive(link.path) ? 'text-gold bg-gold/10' : 'text-gray-600 hover:text-gold hover:bg-gray-50'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            </div>
          </nav>

          {/* Desktop Icons */}
          <div className="hidden md:flex items-center space-x-4 lg:space-x-6">
            <Link to="/cart" className="text-gray-600 hover:text-gold transition-colors relative">
              <ShoppingBag className="w-5 h-5" />
              {totalItems > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </Link>
            <Link
              to={user ? '/account' : '/login'}
              className="text-gray-600 hover:text-gold transition-colors"
            >
              <User className="w-5 h-5" />
            </Link>
          </div>

          {/* Mobile/Tablet Menu Button */}
          <button
            className="md:hidden text-gray-600 hover:text-gold transition-colors"
            onClick={toggleMenu}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            className="md:hidden bg-white border-t"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="container mx-auto px-4 py-4">
              <nav className="flex flex-col space-y-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`text-sm font-medium transition-colors ${
                      isActive(link.path) ? 'text-gold' : 'text-gray-600 hover:text-gold'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                ))}
              </nav>
              <div className="flex items-center space-x-6 mt-6">
                <button className="text-gray-600 hover:text-gold transition-colors">
                  <Search className="w-5 h-5" />
                </button>
                <Link to="/wishlist" className="text-gray-600 hover:text-gold transition-colors">
                  <Heart className="w-5 h-5" />
                </Link>
                <Link to="/cart" className="text-gray-600 hover:text-gold transition-colors relative">
                  <ShoppingBag className="w-5 h-5" />
                  {totalItems > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                      {totalItems}
                    </span>
                  )}
                </Link>
                <Link
                  to={user ? '/account' : '/login'}
                  className="text-gray-600 hover:text-gold transition-colors"
                >
                  <User className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
 